import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import { Tabs, Tab } from 'react-bootstrap/lib';
import { Condition } from '@upstart/harmony/lib/constants';
import { isDealerTrackDMSConfigured } from '../../util/dmsUtils';
import SidebarContainer from '../SidebarContainer';
import DealerNav from '../../components/DealerNav';
import LoadingOverlay from '../../components/LoadingOverlay';
import useAccessoryImages from './useAccessoryImages';
import AccessoryConfig from './AccessoryConfig';
import AccessoryMapping from './AccessoryMapping';
import ErrorMessage from '../../components/ErrorMessage';
import dealersActions from '../../redux/dealers/actions';
import accessoryActions from '../../redux/accessories/actions';
import dtEntitiesActions from '../../redux/dtEntities/actions';
import { AccessoryType } from '../../util/constants';
import './Accessory.scss';

const parseValue = num => {
  if (!num || Number.isNaN(num)) {
    return 0;
  }
  return parseFloat(num) || 0;
};

function Accessory({
  actions,
  accessoryConfig,
  dealerTrackEntities,
  dealer,
  error,
  fetching,
  match,
}) {
  const [key, setKey] = useState('config');
  const [accessory, setAccessory] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [images, AccessoryImages] = useAccessoryImages({
    accessory,
  });

  const [dtEntities, setDTEntities] = useState(null);

  const { dealerId, id: accessoryId } = match.params;
  const formRef = useRef(null);
  const submitRef = useRef(null);
  const accessories = (accessoryConfig || {}).accessories || [];

  useEffect(() => {
    if (error) {
      setErrorMessage(
        error.error || 'An unknown error occurred, please try again.',
      );
    }
  }, [error]);

  useEffect(() => {
    if (match.params.id && accessoryConfig) {
      setAccessory(accessories.find(a => a.id === accessoryId));
    } else if (!match.params.id) {
      setAccessory({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoryConfig]);

  useEffect(() => {
    actions.dealerRequest({ dealerId });
    actions.accessoryConfigRequest({ dealerId });
  }, [actions, dealerId]);

  // grab the DTEs for the dealer when the dealer changes
  useEffect(() => {
    if (isDealerTrackDMSConfigured(dealer)) {
      actions.dtEntityRequest({ dealerId });
    }
  }, [dealer]);

  useEffect(() => {
    setDTEntities((dealerTrackEntities || {}).dtEntities || []);
  }, [dealerTrackEntities]);

  const onSave = e => {
    e.preventDefault();
    setSubmitted(true);

    // Scroll to top
    document.querySelector('.dealers-table').scrollTo(0, 0);

    const newImages = images.filter(img => !!img.file).map(img => img.file);
    const existingImages = images.filter(img => !img.file).map(img => img.src);
    const primaryImageIdx = images.findIndex(img => img.isPrimary);

    const newAccessory = { ...accessory };
    newAccessory.type = AccessoryType.STATIC;
    newAccessory.price = parseValue(accessory.price);
    newAccessory.discount = parseValue(accessory.discount);
    newAccessory.images = existingImages;

    if (
      newAccessory.conditions?.includes(Condition.NEW) &&
      isEmpty(newAccessory.mapping)
    ) {
      setErrorMessage('Please specify Make / Model / Trim under Mapping.');
    } else {
      // Use form data to upload data and images
      const formData = new FormData();
      newImages.forEach(img => formData.append('images', img));
      formData.append('accessory', JSON.stringify(newAccessory));
      formData.append('primaryImageIdx', primaryImageIdx);

      actions.accessoryUpdateRequest({
        accessoryId,
        dealerId,
        formData,
      });
    }
  };

  return (
    <SidebarContainer title="Accessories" logo={dealer.logo}>
      <div className="dealers-table">
        <DealerNav />
        <div className="accessories-container">
          <LoadingOverlay active={fetching}>
            {!fetching && submitted && errorMessage && (
              <ErrorMessage error={errorMessage} />
            )}
            {dealer.id && accessory && (
              <form ref={formRef} className="accessory-form" onSubmit={onSave}>
                <Tabs
                  id="accessories-tabs"
                  activeKey={key}
                  onSelect={newKey => {
                    if (!formRef.current.checkValidity()) {
                      submitRef.current.click();
                    } else {
                      setKey(newKey);
                    }
                  }}
                >
                  <Tab eventKey="config" title="Config">
                    <AccessoryConfig
                      accessoryConfig={accessoryConfig}
                      accessory={accessory}
                      setAccessory={setAccessory}
                      dealer={dealer}
                      dtEntities={dtEntities}
                    />
                  </Tab>
                  <Tab eventKey="images" title="Images">
                    <AccessoryImages />
                  </Tab>
                  <Tab eventKey="mapping" title="Mapping">
                    <AccessoryMapping
                      dealer={dealer}
                      accessory={accessory}
                      setAccessory={setAccessory}
                    />
                  </Tab>
                </Tabs>
                <div className="accessory-footer">
                  <Link
                    className="btn btn-default"
                    to={`/${dealer.id}/accessories`}
                  >
                    Cancel
                  </Link>
                  <button
                    ref={submitRef}
                    type="submit"
                    className="btn btn-primary"
                  >
                    Save
                  </button>
                </div>
              </form>
            )}
          </LoadingOverlay>
        </div>
      </div>
    </SidebarContainer>
  );
}

Accessory.propTypes = {
  accessoryConfig: PropTypes.object,
  actions: PropTypes.object,
  dealer: PropTypes.object,
  error: PropTypes.object,
  fetching: PropTypes.bool,
  match: PropTypes.object,
  dtEntities: PropTypes.object,
};

const mapStateToProps = ({ accessories, dealers, dtEntities }) => ({
  accessoryConfig: accessories.accessoryConfig,
  dealer: dealers.dealer,
  dealerTrackEntities: dtEntities,
  error: accessories.error,
  fetching: dealers.fetching || accessories.fetching,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      ...dealersActions,
      ...accessoryActions,
      ...dtEntitiesActions,
    },
    dispatch,
  ),
});

export default connect(mapStateToProps, mapDispatchToProps)(Accessory);
